import { SessionsSchema } from "mock/schemas"
import { BaseMmsMockService } from "services/mms/mock/entities-services/BaseMockService"
import { CommunicationService } from "services/mms/mock/CommunicationService"
import { FetchMockProps } from "types/mms-base"
import { SessionsServiceInterface } from "interfaces/sessions-service"
import { SessionInfo } from "types/sessions"

export class MockSessionsService
    extends BaseMmsMockService
    implements SessionsServiceInterface
{
    constructor(communicationService: CommunicationService) {
        super(communicationService)
    }

    padWithZero(str: string): string {
        return str.length < 2 ? "0" + str : str
    }

    formatTimeWithPadding(date: Date): string {
        const hour = date.getHours().toString()
        const minutes = date.getMinutes().toString()
        const hourWithPadding = this.padWithZero(hour)
        const minutesWithPadding = this.padWithZero(minutes)
        return `${hourWithPadding}:${minutesWithPadding}`
    }

    formatDuration(starting_date: Date, end_date: Date): string {
        const milliDiff: number = end_date.getTime() - starting_date.getTime()
        const totalMinutes = Math.floor(milliDiff / 60000)
        const totalHours = Math.floor(milliDiff / 3600000)
        const remMinutes = totalMinutes % 60
        let duration: string = ""
        if (totalHours >= 1) {
            duration += totalHours + "h"
        }
        duration += this.padWithZero(remMinutes.toString()) + " min"
        return duration
    }

    private mapDate = (session: SessionsSchema) => {
        const sessionDate = new Date(session.start)
        let sessionDateStr = `Le ${sessionDate.toLocaleDateString()}`
        let sessionTimeStr = "\n"
        const startingTime = this.formatTimeWithPadding(sessionDate)
        if (session.status === "OVER") {
            const endSessionDate = new Date(session.end)
            sessionTimeStr += `De ${startingTime} à ${this.formatTimeWithPadding(endSessionDate)}`
            sessionTimeStr += `\n${this.formatDuration(sessionDate, endSessionDate)}`
        } else {
            sessionTimeStr += `À ${startingTime}`
        }
        sessionDateStr += sessionTimeStr
        return sessionDateStr
    }

    private mapSession = (session: SessionsSchema): SessionInfo => {
        const driver = this.communicationService.drivers.find(
            (driver) => driver.id == session.driver_id
        )
        const rfid = this.communicationService.rfidCards.find(
            (card) => card.id == session.rfid_id
        )
        const rfid_number = rfid?.number || ""
        const driver_name = driver
            ? `${driver.firstName} ${driver.lastName}`
            : ""
        const station = this.communicationService.stations.find(
            (station) => station.id == session.station_id
        )
        const station_address = station
            ? `${station.address}`
            : ""

        return {
            id: session.id,
            status: session.status,
            formatted_date: this.mapDate(session),
            starting_date: new Date(session.start).getTime(),
            stopped_date: session.end ? new Date(session.end).getTime() : 0,
            rfid_number: rfid_number,
            card_holder: driver_name,
            station_name: station?.name ?? "",
            station_address: station_address,
            kwh: session.kwh,
            price: session.price,
            triggered_alerts: [],
        }
    }

    useGetSessions = (): FetchApiProps => {
        const datas: FetchMockProps = {
            mockStatus: 200,
            delay: this.delay,
            controllerFn: ({ user }) => {
                const sessionsFromSelfEntity =
                    this.communicationService.sessions
                        .map((d) => ({ ...d }))
                        .filter((d) => d.entity_id === user?.entity_id)
                return {
                    data: sessionsFromSelfEntity.map(this.mapSession),
                }
            },
        }

        return this.get(datas)
    }

    useGetMonthConsumption = (timestamp: string): FetchApiProps => {
        const currentDate = new Date(timestamp)
        const datas: FetchMockProps = {
            mockStatus: 201,
            delay: this.delay,
            controllerFn: ({ user }) => {
                const sessionsFromSelfEntity =
                    this.communicationService.sessions
                        .map((d) => ({ ...d }))
                        .filter((d) => d.entity_id === user?.entity_id)
                return {
                    data: sessionsFromSelfEntity.map(this.mapSession).filter((d) => {
                        const startTs = 'startingDate' in d ? d.startingDate : d.starting_date
                        const dStart = new Date(startTs)
                        return dStart.getMonth() == currentDate.getMonth() &&
                            dStart.getFullYear() == currentDate.getFullYear()
                    }),
                }
            },
        }
        return this.get(datas)
    }

    useGetTotalRefillSession = (startDate: Date, endDate: Date): FetchApiProps => {
        const datas: FetchMockProps = {
            mockStatus: 200,
            delay: this.delay,
            controllerFn: ({ user }) => {
                const sessionsFromSelfEntity = this.communicationService.sessions
                    .map((d) => ({ ...d }))
                    .filter((d) => d.entity_id === user?.entity_id)
                    .filter((session) => {
                        const sessionStartDate = new Date(session.start);
                        return sessionStartDate >= startDate && sessionStartDate <= endDate;
                    });

                const totalRefill = sessionsFromSelfEntity.length;
                return {
                    data: totalRefill,
                };
            },
        };
        return this.get(datas);
    };

    useGetAverageKilowatt = (startDate: Date, endDate: Date): FetchApiProps => {
        const datas: FetchMockProps = {
            mockStatus: 200,
            delay: this.delay,
            controllerFn: ({ user }) => {
                const sessionsFromSelfEntity =
                    this.communicationService.sessions
                        .map((d) => ({ ...d }))
                        .filter((d) => d.entity_id === user?.entity_id)
                        .filter((session) => {
                            const sessionStartDate = new Date(session.start);
                            return sessionStartDate >= startDate && sessionStartDate <= endDate;
                        });

                const totalKwh = sessionsFromSelfEntity.reduce((acc, session) => {
                    return acc + session.kwh;
                }, 0);
                const averageKwh = totalKwh / sessionsFromSelfEntity.length;
                return {
                    data:  averageKwh.toFixed(2),
                }
            },
        }
        return this.get(datas)
    }

    useGetTotalKilowatt = (startDate: Date, endDate: Date): FetchApiProps => {
        const datas: FetchMockProps = {
            mockStatus: 200,
            delay: this.delay,
            controllerFn: ({ user }) => {
                const sessionsFromSelfEntity =
                    this.communicationService.sessions
                        .map((d) => ({ ...d }))
                        .filter((d) => d.entity_id === user?.entity_id)
                        .filter((session) => {
                            const sessionStartDate = new Date(session.start);
                            return sessionStartDate >= startDate && sessionStartDate <= endDate;
                        });
                const totalKwh = sessionsFromSelfEntity.reduce((acc, session) => {
                    return acc + session.kwh;
                }, 0);
                return {
                    data:  totalKwh,
                }
            },
        }
        return this.get(datas)
    }

    useGetAverageTime = (timestampStart: string, timestampEnd: string): FetchApiProps => {
        const start = new Date(timestampStart)
        const end = new Date(timestampEnd)
        const datas: FetchMockProps = {
            mockStatus: 201,
            delay: this.delay,
            controllerFn: ({ user }) => {
                const sessionsFromSelfEntity =
                    this.communicationService.sessions
                        .map((d) => ({ ...d }))
                        .filter((d) => d.entity_id === user?.entity_id)
                        .filter((session) => {
                            const sessionStartDate = new Date(session.start);
                            return sessionStartDate.getTime() >= start.getTime() && sessionStartDate.getTime() <= end.getTime();
                        });
                const startDates: Date[] = [];
                const endDates: Date[] = [];
                sessionsFromSelfEntity.forEach(s => {
                    const startDate = new Date(s.start);
                    const endDate = s.end.length !== 0 ? new Date(s.end) : null;
                    if (endDate !== null) {
                        startDates.push(startDate);
                        endDates.push(endDate);
                    }
                });

                const differences = startDates.map((startDate, index) => {
                    const endDate = endDates[index];
                    if (endDate instanceof Date && startDate instanceof Date) {
                        return endDate.getTime() - startDate.getTime();
                    }
                }).filter(diff => diff !== null);

                const averageDifference = differences.reduce((acc, diff) => {
                    if (acc) {
                        return acc + diff!;
                    } else {
                        return diff!;
                    }
                }, 0);
                if (averageDifference && differences.length !== 0 ) {
                    const averageMinutes = Math.floor((averageDifference / differences.length) / (1000 * 60));
                    const averageSeconds = Math.floor(((averageDifference / differences.length) % (1000 * 60)) / 1000);
                    return {
                        data: `${averageMinutes}:${averageSeconds}`,
                    };
                } else {
                    return {
                        data: null,
                    };
                }
            },
        }
        return this.get(datas)
    }
}
